import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { FaTimes, FaPlus, FaExpandAlt, FaHistory, FaChevronLeft } from 'react-icons/fa';

import { AuthContext } from '../../context/AuthContext';
import ChatComposer from './ChatComposer';
import ChatHistory from './ChatHistory';
import ChatTranscript from './ChatTranscript';
import InnaAvatar from './InnaAvatar';
import useChat from '../../hooks/useChat';

/**
 * INNA, docked bottom-right on every authenticated page.
 *
 * Mounted once in App, outside the router Switch, so the conversation survives
 * navigation. It shares its thread with the full-page view through the same
 * local-storage key, so opening /chat continues whatever was started here.
 */
const ChatWidget = () => {
    const { state: authState } = useContext(AuthContext);
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const {
        messages,
        send,
        loading,
        busy,
        stream,
        conversationId,
        pendingConversationId,
        conversations,
        startNew,
        selectConversation,
        deleteConversation,
        clearHistory
    } = useChat();

    // Mark unread only for answers that land while the panel is shut.
    useEffect(() => {
        if (!isOpen && messages.length && messages[messages.length - 1].role === 'assistant') {
            setHasUnread(true);
        }
    }, [messages, isOpen]);

    useEffect(() => {
        if (isOpen) {
            setHasUnread(false);
        } else {
            // Reopening lands on the conversation rather than wherever the
            // history list happened to be left.
            setShowHistory(false);
        }
    }, [isOpen]);

    // Escape closes the panel, as it does for the app's modals - but backs out
    // of the history list first, so it never skips a step.
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handleKey = (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            if (showHistory) {
                setShowHistory(false);
            } else {
                setIsOpen(false);
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, showHistory]);

    // Signed out, or already on the full-page view - nothing to float here.
    if (!authState.user || authState.isLoading || location.pathname.startsWith('/chat')) {
        return null;
    }

    const handleSend = (text) => {
        setIsOpen(true);
        send(text);
    };

    const handleNew = () => {
        startNew();
        setShowHistory(false);
    };

    const handleSelect = (id) => {
        selectConversation(id);
        setShowHistory(false);
    };

    return (
        <>
            {isOpen && (
                <section className="chat-panel" role="dialog" aria-label="INNA">
                    <header className="chat-panel-header">
                        {showHistory ? (
                            <div className="chat-panel-title">
                                <button
                                    type="button"
                                    className="chat-icon-button mr-2"
                                    onClick={() => setShowHistory(false)}
                                    title="Back to conversation"
                                    aria-label="Back to conversation"
                                >
                                    <FaChevronLeft />
                                </button>
                                <span>History</span>
                            </div>
                        ) : (
                            <div className="chat-panel-title">
                                <InnaAvatar size={32} className="mr-2" />
                                <span>
                                    INNA
                                    <small className="chat-panel-subtitle">Warehouse assistant</small>
                                </span>
                            </div>
                        )}
                        <div className="chat-panel-actions">
                            {!showHistory && (
                                <button
                                    type="button"
                                    className="chat-icon-button"
                                    onClick={() => setShowHistory(true)}
                                    disabled={!conversations.length}
                                    title="History"
                                    aria-label="Conversation history"
                                >
                                    <FaHistory />
                                </button>
                            )}
                            <button
                                type="button"
                                className="chat-icon-button"
                                onClick={handleNew}
                                disabled={busy || !messages.length}
                                title="New convo"
                                aria-label="New convo"
                            >
                                <FaPlus />
                            </button>
                            <Link
                                to="/chat"
                                className="chat-icon-button"
                                title="Open full page"
                                aria-label="Open full page"
                            >
                                <FaExpandAlt />
                            </Link>
                            <button
                                type="button"
                                className="chat-icon-button"
                                onClick={() => setIsOpen(false)}
                                title="Close"
                                aria-label="Close assistant"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    </header>

                    {showHistory ? (
                        <div className="chat-history-panel">
                            <ChatHistory
                                conversations={conversations}
                                currentId={conversationId}
                                pendingConversationId={pendingConversationId}
                                onSelect={handleSelect}
                                onDelete={deleteConversation}
                                onClearAll={clearHistory}
                            />
                        </div>
                    ) : (
                        <>
                            <ChatTranscript
                                messages={messages}
                                loading={loading}
                                stream={stream}
                                onSuggestion={handleSend}
                                compact
                            />

                            <ChatComposer
                                onSend={send}
                                loading={busy}
                                busyElsewhere={busy && !loading}
                                autoFocus
                                inputId="chat-widget-input"
                            />
                        </>
                    )}
                </section>
            )}

            <button
                type="button"
                className={classnames('chat-launcher', { 'chat-launcher-open': isOpen })}
                onClick={() => setIsOpen((open) => !open)}
                aria-label={isOpen ? 'Close INNA' : 'Ask INNA'}
                aria-expanded={isOpen}
            >
                {isOpen ? <FaTimes /> : <InnaAvatar size={44} />}
                {hasUnread && !isOpen && <span className="chat-launcher-badge" />}
            </button>
        </>
    );
};

export default ChatWidget;
